import React, { useState, useRef, useEffect, useCallback } from "react";
import { IoMdMore } from "react-icons/io";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReportDialog from "../Components/ReportDialog/ReportDialog";
import { getRelativeTime } from "../helpers/helper";
import { PostsService } from "../../services";

// Pin Icon Component
const PinIcon = ({ size = 20, color = "#6a5737" }) => (
    <div
        style={{
            width: size,
            height: size,
            backgroundColor: color,
            mask: `url(/community/images/pin.png) no-repeat center / contain`,
            WebkitMask: `url(/community/images/pin.png) no-repeat center / contain`,
            display: 'inline-block'
        }}
    />
);
const PostHeader = ({ post, postId, onCommentDisable, categories, selectedCategory = null, showToast, onEditPost, onDeletePost }) => {
    const currentUser = useSelector((state) => state.projectInfo.uid);    
    const [showOptions, setShowOptions] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [checkingReportStatus, setCheckingReportStatus] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const user = post?.user;

    const reportStatus = useSelector((state) =>
        state.posts.reportStatus && state.posts.reportStatus[postId]
    );

    const toggleOptions = () => {
        setShowOptions((prev) => !prev);
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const navigateToProfile = () => {
        navigate(`/community/profiles/${user?.user_id || "unknown"}`);
    };

    // Ensure we have a fallback for showToast if it's not provided
    const handleShowToast = (message, type) => {
        if (typeof showToast === 'function') {
            showToast(message, type);
        } else {
            // If showToast is not available, use console.log as fallback
            console.log(`${type}: ${message}`);
        }
    };
    const handleReportSubmit = useCallback(async (reason) => {
        try {
            const response = await PostsService.reportPost(postId, reason);
            setShowReportDialog(false);
            if (response.data.success) {
                handleShowToast("Post reported successfully", "success");
            } else {
                handleShowToast("Failed to report Post. Please try again.", "error");
            }

        } catch (err) {
            setShowReportDialog(false);
            if (err.response.data?.alreadyReported) {
                handleShowToast("You have already reported this Post.", "warning");
            } else {
                handleShowToast("Failed to report Post. Please try again.", "error");
            }
        }
    }, []);

    const handleEditPost = () => {
        setShowOptions(false);
        if (onEditPost) {
            onEditPost(post, postId);
        }
    };

    const handleDeletePost = () => {
        setShowOptions(false);
        setIsDeleting(true);

        if (onDeletePost) {
            onDeletePost(postId)
                .then(() => {
                    handleShowToast("Post deleted successfully", "success");
                })
                .catch((error) => {
                    handleShowToast("Failed to delete post: " + error.message, "error");
                })
                .finally(() => {
                    setIsDeleting(false);
                });
        } else {
            setIsDeleting(false);
        }
    };

    return (
        <div className="post-header">
            <div className="postleft" onClick={navigateToProfile} style={{ cursor: "pointer" }}>
                {!user ? (
                    <>
                        <div className="skeleton-avatar" style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(90deg,#f0f0f0 0%,#f8f8f8 50%,#f0f0f0 100%)', marginRight: 12 }}></div>
                        <div className="header-info">
                            <div className="header-info-top">
                                <div className="skeleton-username" style={{ width: 80, height: 16, background: '#f0f0f0', borderRadius: 4, marginBottom: 4 }}></div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <img
                            src={user?.user_image || "https://gizmeon.mdc.akamaized.net/PUB-50119/post/1749720612786.png"}
                            alt="Profile"
                            className="profile-pic"
                        />
                        <div className="header-info">
                            <div className="header-info-top">
                                <span className="username">{user?.user_name || "Unknown User"}</span>
                                {selectedCategory === null && post?.category && (
                                    <span className="category">
                                        in ⚪ {categories.find((category) => category.id === post?.category)?.name}
                                    </span>
                                )}
                            </div>
                            <span className="post-date">{getRelativeTime(new Date(post?.created_at))}</span>
                        </div>
                    </>
                )}
            </div>
            <div className="headerright" ref={menuRef}>
                {
                    post.is_pinned 
                    ? <span className="pinned-badge"><PinIcon size={20} /></span> 
                    : <IoMdMore size={24} onClick={toggleOptions} className="more-icon" />
                }

                {/* Options Menu */}
                {showOptions && (
                    <div className="options-menu">
                        {post?.user.user_id === Number(currentUser) && (
                            <>
                                <button onClick={() => postId ? onCommentDisable(postId) : onCommentDisable()}>
                                    {post?.comments_disabled ? "Enable Comments" : "Disable Comments"}
                                </button>
                                <button onClick={handleEditPost}>
                                    Edit Post
                                </button>
                                <button
                                    onClick={handleDeletePost}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? "Deleting..." : "Delete Post"}
                                </button>
                            </>
                        )}
                        {user?.user_id !== Number(currentUser) && (
                            <button
                                onClick={() => setShowReportDialog(true)}
                                disabled={checkingReportStatus}
                            >
                                {checkingReportStatus ? "Checking..." : "Report Post"}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Report Dialog */}
            <ReportDialog
                isOpen={showReportDialog}
                onClose={() => setShowReportDialog(false)}
                onSubmit={handleReportSubmit}
                isSubmitting={reportStatus?.loading}
                type="post"
            />
        </div>
    );
};

export default PostHeader;
